import * as React from "react";
import {cx}       from "emotion";

import "./tab-designations.scss";

import {consumeContext} from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {UseRef}         from "@Common/react/use-ref";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {ButtonSpinner}  from "@Common/ui-components/loading-spinners/button-spinner";


import {GalleryImage}      from "../components/gallery-image";
import {LoadingSpinner}    from "../components/loading-spinner";
import {MultiSelectFooter} from "../components/multi-select-footer";
import {NoticeMessage}     from "../components/notice-message";
import {Paginator}         from "../components/paginator";


export const TabDesignations = ({
    modal,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    templateId,
    useEpsDesignations    = false,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    templateId            : number,
    useEpsDesignations    : boolean,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        isImageLoadingToEditor  : boolean,
        pageNumber              : number,
        searchString            : string,
    }>>) => UseState({
        defaultValue: {
            isImageLoadingToEditor: false,
            pageNumber: 1,
            searchString: "",
        },
        next,
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {

        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-content designation-results">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // load images
    .then("loaderPhotos", ({apis, state}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImage[]>>) => Load2({
        fetch: () => apis.next.gallery.designations.getImages(templateId, {isBuild3Order: useEpsDesignations}),
        captureException: true,
        next,
    }))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // render the images list
    .then("imagesContents", ({state, company, loaderPhotos, includePagination}, next:Link<JSX.Element>) => {
         // Perform client-side filtering based on searchString
        const searchTerm = state.value.searchString.toLowerCase();
        let filteredImages = loaderPhotos.value?.filter((photo) =>
            photo.name.toLowerCase().includes(searchTerm)
        ) || [];

        // Apply pagination if necessary
        if (filteredImages.length > 0) {
            filteredImages = filteredImages.slice(
                (state.value.pageNumber - 1) * numResultsPerPage,
                state.value.pageNumber * numResultsPerPage
            );
        }
        // return until photo data exists
        if (!loaderPhotos.value || loaderPhotos.loading) {
            return next(<LoadingSpinner text="Loading Designations"/>);
        }

        let photos = searchTerm ? filteredImages : loaderPhotos.value;
        if (includePagination) photos = photos.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);
        //if no designations match the search input then add a message
        if (searchTerm && !photos?.length) return next(<NoticeMessage text="No matching designations found."/>);

        // add a message for empty designations gallery
        if (!photos?.length) return next(<NoticeMessage text="There are currently no designations available."/>);

        // render the image list
        return next(<>
            {photos.map((photo) => (
            <GalleryImage
                key={photo.id}
                id={photo.id}
                imageName={photo.name}
                imageAttributionData={photo.attribution}
                thumbnailUrl={photo.thumbnailUrl}
                viewType={viewType}
                fileType={photo.fileType}
                fileSize={photo.fileSize}
                width={photo.width}
                height={photo.height}
                areaWidth={imageArea?.widthInches}
                areaHeight={imageArea?.heightInches}
                photoQualitySettings={imageArea?.photoQualitySettings}
                allowLowQuality={true}
                isDesignationImage={true}
                disabled={photo.fileType === "svg" && disallowSvgImages}
                selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "designation" && x.imageId === photo.id)+1}
                onClick={() => {

                    // apply the image for single select mode
                    if (singleSelectOptions) {

                        // set the image as applying
                        state.onChange({
                            ...state.value,
                            isImageLoadingToEditor: true,
                        });

                        // apply the image
                        singleSelectOptions.onApply(imageArea, "designation", photo.id, photo.printUrl, modal.close);

                    }

                    // select the image for multi-select mode
                    else if (multiSelectOptions) {

                        multiSelectOptions.onSelect({
                            assetType: "designation",
                            imageId: photo.id,
                            imagePath: photo.printUrl,
                            thumbnailUrl: photo.thumbnailUrl,
                        });

                    }

                }}
            />))}
        </>);
    })

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the tab contents
    .then(({state, imagesContents, loaderPhotos, includePagination, resultsAreaRef}) => (<>
        <div className={cx("tab-designations-ptb tab-content designation-gallery", "viewtype-"+viewType)}>
            <div className="results" ref={ref => resultsAreaRef.set(ref)}>
                <div className="search-field">
                    <input className="field"
                        xd-qa-type="textbox"
                        xd-qa-name="ccgallery photo designations search field"
                        placeholder="Search designation Images"
                        maxLength={200}
                        value={state.value.searchString}
                        onChange={event => {
                            const searchValue = event.target.value;
                            if (!loaderPhotos.loading) {
                                state.onChange({
                                    ...state.value,
                                    searchString: searchValue,
                                    pageNumber: 1, // Reset to the first page
                                });
                            }
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && !loaderPhotos.loading) {
                                state.onChange({ ...state.value }); // Trigger refresh
                            }
                        }}
                    />
                    <button type="submit" className="button" disabled={loaderPhotos.loading}
                        xd-qa-type="button" xd-qa-name="ccgallery photo designations search submit"
                        onClick={() => {
                            if (!loaderPhotos.loading) {
                                state.onChange({ ...state.value,pageNumber: 1 }); // Trigger refresh
                            }
                        }}
                    >
                        {(!loaderPhotos.loading || !state.value.searchString) ? // don't show spinner if no search string
                            "Search" : (
                            <ButtonSpinner size={20} spinnerColor="#ffffff"/>
                        )}
                    </button>
                </div>
                <div className="source-images">
                    {imagesContents}
                </div>
            </div>
            {includePagination && (
                <Paginator
                    numItems={loaderPhotos.value?.length}
                    pageNumber={state.value.pageNumber}
                    itemsPerPage={numResultsPerPage}
                    onPaginateNumResults={numResults => {
                        resultsAreaRef.get().scrollTop = 0;
                        onPaginateNumResults(numResults);
                    }}
                    onPaginatePageNumber={pageNumber => {
                        resultsAreaRef.get().scrollTop = 0;
                        state.onChange({...state.value, pageNumber});
                    }}
                />
            )}
        </div>
        {multiSelectOptions && (
            <MultiSelectFooter
                maxSelections={multiSelectOptions.maxSelections}
                numSelected={multiSelectOptions.selectedImages.length}
                onApply={() => {
                    state.onChange({...state.value, isImageLoadingToEditor: true});
                    multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
                }}
                onClear={multiSelectOptions.onClearSelections}
            />
        )}
    </>))

    .run()

);
